import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Play, ArrowRight } from "lucide-react";

export const Player = ({ testId, poster, sources, className, wrapperClass = "" }) => {
  const ref = useRef(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      const v = ref.current;
      if (v) setBlocked(v.paused && v.readyState >= 2);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={`relative ${wrapperClass}`}>
      <video
        ref={ref}
        data-testid={testId}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        onPlay={() => setBlocked(false)}
        className={className}
      >
        {sources.map(([src, type]) => (
          <source key={src} src={src} type={type} />
        ))}
      </video>
      {blocked && (
        <button
          data-testid={`${testId}-play-fallback`}
          onClick={() => ref.current?.play()}
          className="absolute inset-0 flex items-center justify-center bg-black/40"
          aria-label="Lire la vidéo"
        >
          <span className="w-16 h-16 rounded-full bg-[#CCFF00] flex items-center justify-center hover:bg-[#b3e600] transition-colors duration-300">
            <Play className="w-7 h-7 text-black fill-black ml-1" />
          </span>
        </button>
      )}
    </div>
  );
};

export default function VideoSection() {
  const { t } = useLang();
  const scrollToForm = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section data-testid="video-section" className="relative py-16 lg:py-20 border-t border-white/5 overflow-hidden">
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,255,0,0.05) 0%, transparent 65%)" }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-2"
        >
          <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#CCFF00] mb-4">{t.video.kicker}</p>
          <h2 className="font-display font-black tracking-tighter leading-none text-4xl sm:text-5xl text-white mb-6">
            {t.video.title}
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-4">{t.video.desc}</p>

          <div className="border-t border-white/5 mt-8 mb-10">
            {t.video.beats.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                className="flex gap-4 py-4 border-b border-white/5 group"
              >
                <span className="font-mono2 text-[10px] tracking-widest text-zinc-600 pt-1.5">0{i + 1}</span>
                <div>
                  <p className="font-display font-bold text-lg text-white group-hover:text-[#CCFF00] transition-colors duration-300">{b.title}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.button
            data-testid="video-cta-button"
            onClick={scrollToForm}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-sm rounded-sm px-8 py-4 hover:bg-[#b3e600] shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] transition-colors duration-300"
          >
            {t.video.cta}
            <ArrowRight className="w-4 h-4" />
          </motion.button>
          <Link
            to="/demo"
            data-testid="demo-page-link"
            className="inline-flex items-center gap-2 mt-5 font-mono2 text-[10px] tracking-widest uppercase text-zinc-400 hover:text-[#CCFF00] transition-colors duration-300"
          >
            {t.video.liveLink}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-3"
        >
          <div className="neon-trace bg-[#0A0A0A] p-2 sm:p-3 shadow-[0_40px_120px_-30px_rgba(204,255,0,0.12)]">
            <Player
              testId="demo-video"
              poster="/hitmax-demo-poster.jpg"
              sources={[
                ["/hitmax-demo.mp4", "video/mp4"],
                ["/hitmax-demo.webm", "video/webm"],
              ]}
              wrapperClass="hidden md:block"
              className="w-full rounded-sm aspect-video object-cover bg-black"
            />
            <Player
              testId="demo-video-mobile"
              poster="/hitmax-demo-mobile-poster.jpg"
              sources={[
                ["/hitmax-demo-mobile.mp4", "video/mp4"],
                ["/hitmax-demo-mobile.webm", "video/webm"],
              ]}
              wrapperClass="block md:hidden"
              className="w-full max-w-[320px] mx-auto rounded-sm aspect-[9/16] object-cover bg-black"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
